import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { humanizeColumn } from '../../utils/pitchbookDiff';
import { toast } from 'sonner';
import { Database, ExternalLink, Loader2, Pencil, RefreshCw, X, Check } from 'lucide-react';
import UpdateFromPitchBookDialog from './UpdateFromPitchBookDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Section grouping (same rules as before, keyed on PitchBook column name) ──

const SECTION_RULES = [
    ['Role & Employer',         /^Primary(Company|Position)/i],
    ['Primary Contact',         /^PrimaryContact/i],
    ['Status & Ownership',      /Status|Universe|InvestorType|Ownership|Financing|Fundraising/i],
    ['Funds & Capital',         /Fund|AUM|DryPowder|Raised|Valuation|RoundAmount/i],
    ['Financials',              /Revenue|Profit|Income|EBIT|Debt|EnterpriseValue|Fiscal|PeriodEnd/i],
    ['Investment Activity',     /^Total|Portfolio|Exits|InvestmentProfessional/i],
    ['Industry & Focus',        /Industr|Vertical|EmergingSpaces|Keywords|TradeAssociations/i],
    ['Corporate Structure',     /Parent|Exchange|Ticker|AlternateOffice/i],
    ['Location & Contact Info', /^HQ|^Location$|^(Phone|MobilePhone|Fax|Email)$/i],
    ['Web & Social',            /URL|Website|ProfileLink|ProfileDataSource/i],
    ['Identity & Background',   /Name|Description|YearFounded|Employees|Prefix|Gender|Biography|University|YearOfBirth/i],
];
const OTHER_SECTION = 'Other';

function sectionFor(column) {
    for (const [section, pattern] of SECTION_RULES) {
        if (pattern.test(column)) return section;
    }
    return OTHER_SECTION;
}

function groupBySection(fields) {
    const buckets = new Map();
    for (const f of fields) {
        const s = sectionFor(f.column);
        if (!buckets.has(s)) buckets.set(s, []);
        buckets.get(s).push(f);
    }
    const order = [...SECTION_RULES.map(([s]) => s), OTHER_SECTION];
    return order.filter(s => buckets.has(s)).map(s => [s, buckets.get(s)]);
}

// ─── Value display helpers ─────────────────────────────────────────────────────

function displayValue(v) {
    if (v === null || v === undefined || v === '') return null;
    if (Array.isArray(v)) return v.join(', ') || null;
    if (typeof v === 'object') {
        if ('amount_m' in v) {
            const m = Number(v.amount_m);
            if (!Number.isFinite(m)) return null;
            const abs = Math.abs(m);
            if (abs >= 1000) return `$${(m / 1000).toFixed(1).replace(/\.0$/, '')}B`;
            if (abs >= 1) return `$${m.toFixed(1).replace(/\.0$/, '')}M`;
            return `$${(m * 1000).toFixed(0)}K`;
        }
        return JSON.stringify(v);
    }
    return String(v);
}

function isUrl(v) {
    return typeof v === 'string' && /^https?:\/\//i.test(v);
}

// Guess input type from the field name / value
function inputTypeFor(field, value) {
    if (/year|count|employees|investments|exits|portfolio|funds/i.test(field)) return 'number';
    if (/revenue|raised|profit|income|ebitda|ebit|debt|enterprise|aum|powder|round|valuation|fund_size/i.test(field)) return 'number';
    if (/url|website|linkedin|twitter|facebook/i.test(field)) return 'url';
    if (typeof value === 'number') return 'number';
    return 'text';
}

// ─── Single inline-editable field cell ────────────────────────────────────────

function FieldCell({ descriptor, accountId, onSaved }) {
    const { getAuthHeader } = useAuth();
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    const display = displayValue(descriptor.value);
    const itype = inputTypeFor(descriptor.field, descriptor.value);
    const isLink = isUrl(descriptor.value);

    const startEdit = () => {
        setDraft(descriptor.value == null ? '' : String(descriptor.value));
        setEditing(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const cancelEdit = () => setEditing(false);

    const saveEdit = async () => {
        let coerced = draft;
        if (itype === 'number') {
            coerced = draft === '' ? null : Number(draft);
            if (draft !== '' && !Number.isFinite(coerced)) {
                toast.error('Enter a valid number');
                return;
            }
        }
        setSaving(true);
        try {
            await axios.patch(
                `${API}/accounts/${accountId}/pitchbook-field`,
                { field: descriptor.field, value: coerced },
                { headers: getAuthHeader() },
            );
            onSaved(descriptor.field, coerced);
            setEditing(false);
            toast.success(`${humanizeColumn(descriptor.column)} updated`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleKey = (e) => {
        if (e.key === 'Enter' && itype !== 'text') { e.preventDefault(); saveEdit(); }
        if (e.key === 'Escape') cancelEdit();
    };

    return (
        <div className="flex gap-3 min-w-0 group/cell">
            {/* Label */}
            <span className="w-44 text-xs font-semibold tracking-wider uppercase text-stone-400 shrink-0 pt-0.5">
                {humanizeColumn(descriptor.column)}
            </span>

            {/* Value / editor */}
            <div className="flex-1 min-w-0">
                {editing ? (
                    <div className="flex items-center gap-1">
                        <input
                            ref={inputRef}
                            type={itype === 'url' ? 'url' : itype === 'number' ? 'number' : 'text'}
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={handleKey}
                            className="flex-1 text-sm border border-stone-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-stone-500 min-w-0"
                            disabled={saving}
                        />
                        <button
                            onClick={saveEdit}
                            disabled={saving}
                            className="text-emerald-600 hover:text-emerald-700 disabled:opacity-40 p-0.5"
                            title="Save"
                        >
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        </button>
                        <button
                            onClick={cancelEdit}
                            disabled={saving}
                            className="text-stone-400 hover:text-stone-600 p-0.5"
                            title="Cancel"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ) : display !== null ? (
                    <div className="flex items-start gap-1.5 min-w-0">
                        {isLink ? (
                            <a
                                href={descriptor.value}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 break-all hover:underline inline-flex items-start gap-1 min-w-0"
                            >
                                <span className="truncate">{display}</span>
                                <ExternalLink className="w-3 h-3 shrink-0 mt-0.5 opacity-70" />
                            </a>
                        ) : (
                            <span className="text-sm text-stone-700 break-words min-w-0">{display}</span>
                        )}
                        {descriptor.editable && (
                            <button
                                onClick={startEdit}
                                className="opacity-0 group-hover/cell:opacity-100 text-stone-300 hover:text-stone-600 shrink-0 mt-0.5 transition-opacity"
                                title="Edit"
                            >
                                <Pencil className="w-3 h-3" />
                            </button>
                        )}
                    </div>
                ) : (
                    descriptor.editable ? (
                        <button
                            onClick={startEdit}
                            className="text-xs text-stone-300 hover:text-stone-500 italic"
                        >
                            — click to add
                        </button>
                    ) : (
                        <span className="text-xs text-stone-300 italic">—</span>
                    )
                )}
            </div>
        </div>
    );
}

// ─── Section block ─────────────────────────────────────────────────────────────

function FieldSection({ title, fields, accountId, onSaved }) {
    return (
        <section className="overflow-hidden bg-white rounded-lg border border-stone-200">
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50/60">
                <h4 className="text-xs font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {title}
                </h4>
            </div>
            <div className="grid grid-cols-1 gap-y-3 gap-x-8 p-4 md:grid-cols-2">
                {fields.map(f => (
                    <FieldCell
                        key={f.field}
                        descriptor={f}
                        accountId={accountId}
                        onSaved={onSaved}
                    />
                ))}
            </div>
        </section>
    );
}

// ─── Main tab ─────────────────────────────────────────────────────────────────

/**
 * PitchBook tab on the Company detail page.
 *
 * Reads from the REAL company record (accounts collection) via
 * GET /pitchbook/company-fields/{accountId} — never queries the PitchBook
 * index cache for display. Inline edits write back via
 * PATCH /accounts/{accountId}/pitchbook-field.
 *
 * The "Update from PitchBook" button is the ONLY path that touches the index.
 */
export default function PitchBookRecordTab({ crmType, crmId, accountName }) {
    const { getAuthHeader } = useAuth();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);          // { pitchbook_id, fields }
    const [showUpdateDialog, setShowUpdateDialog] = useState(false);

    const load = useCallback(async () => {
        if (!crmId || crmType !== 'account') return;
        setLoading(true);
        try {
            const res = await axios.get(
                `${API}/pitchbook/company-fields/${crmId}`,
                { headers: getAuthHeader() },
            );
            setData(res.data);
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [crmType, crmId]);

    useEffect(() => { load(); }, [load]);

    // Optimistic local update after inline save — avoids a full refetch
    const handleSaved = useCallback((field, value) => {
        setData(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                fields: prev.fields.map(f => f.field === field ? { ...f, value } : f),
            };
        });
    }, []);

    // Contact tab still uses the old read-only view (out of scope)
    if (crmType !== 'account') {
        return <LegacyContactTab crmType={crmType} crmId={crmId} />;
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-40 text-stone-400">
                <Loader2 className="w-5 h-5 animate-spin" />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex flex-col items-center justify-center h-40 bg-stone-50 rounded-xl border border-stone-200 text-stone-400 gap-2">
                <Database className="w-6 h-6" />
                <p className="text-sm">No PitchBook data is linked to this record.</p>
            </div>
        );
    }

    const sections = groupBySection(data.fields);

    return (
        <div className="space-y-4">
            {/* Header row */}
            <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-stone-400">
                    All fields PitchBook can populate for this company.
                    Edit values inline or pull fresh data from PitchBook.
                </p>
                <button
                    onClick={() => setShowUpdateDialog(true)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-stone-200 bg-white text-stone-700 hover:bg-stone-50 hover:border-stone-300 shrink-0 transition-colors"
                >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Update from PitchBook
                </button>
            </div>

            {/* Field sections */}
            <div className="space-y-3">
                {sections.map(([section, fields]) => (
                    <FieldSection
                        key={section}
                        title={section}
                        fields={fields}
                        accountId={crmId}
                        onSaved={handleSaved}
                    />
                ))}
            </div>

            {showUpdateDialog && (
                <UpdateFromPitchBookDialog
                    accountId={crmId}
                    accountName={accountName}
                    defaultPitchbookId={data.pitchbook_id || data.pitchbook_investor_id}
                    onClose={() => setShowUpdateDialog(false)}
                    onCommitted={() => {
                        setShowUpdateDialog(false);
                        load();
                    }}
                />
            )}
        </div>
    );
}

// ─── Legacy read-only contact tab (unchanged, kept in same file) ───────────────

function LegacyContactTab({ crmType, crmId }) {
    const { getAuthHeader } = useAuth();
    const [loading, setLoading] = useState(true);
    const [records, setRecords] = useState([]);

    useEffect(() => {
        if (!crmId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await axios.get(
                    `${API}/pitchbook/record/${crmType}/${crmId}`,
                    { headers: getAuthHeader() },
                );
                if (!cancelled) setRecords(res.data?.records || []);
            } catch {
                if (!cancelled) setRecords([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [crmType, crmId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-40 text-stone-400">
                <Loader2 className="w-5 h-5 animate-spin" />
            </div>
        );
    }
    if (!records.length) {
        return (
            <div className="flex flex-col items-center justify-center h-40 bg-stone-50 rounded-xl border border-stone-200 text-stone-400 gap-2">
                <Database className="w-6 h-6" />
                <p className="text-sm">No PitchBook data is linked to this record.</p>
            </div>
        );
    }
    // Minimal read-only display for contacts
    return (
        <div className="space-y-4">
            {records.map(r => (
                <LegacyRecordCard key={`${r.entity}_${r.pitchbook_id}`} record={r} />
            ))}
        </div>
    );
}

function LegacyRecordCard({ record }) {
    const imported = (record.fields || []).filter(f => f.imported);
    const additional = (record.fields || []).filter(f => !f.imported);
    const sections = groupBySection(additional);
    const importedSections = groupBySection(imported);
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-stone-900 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {record.entity === 'person' ? 'Person profile' : 'Profile'}
                </h3>
                {record.last_updated && (
                    <span className="text-[10px] text-stone-400">PitchBook last updated {record.last_updated}</span>
                )}
            </div>
            {sections.length > 0 && (
                <div className="space-y-3 mb-4">
                    {sections.map(([section, fields]) => (
                        <LegacySection key={section} title={section} fields={fields} />
                    ))}
                </div>
            )}
            {imported.length > 0 && (
                <details className="group">
                    <summary className="text-xs font-medium text-stone-500 cursor-pointer select-none hover:text-stone-700">
                        Fields imported to the CRM record ({imported.length})
                    </summary>
                    <div className="mt-2 space-y-3">
                        {importedSections.map(([section, fields]) => (
                            <LegacySection key={section} title={section} fields={fields} />
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}

function LegacySection({ title, fields }) {
    const visible = fields.filter(f => {
        const v = f.value;
        return v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && !v.length);
    });
    if (!visible.length) return null;
    return (
        <section className="overflow-hidden bg-white rounded-lg border border-stone-200">
            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50/60">
                <h4 className="text-xs font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h4>
            </div>
            <div className="grid grid-cols-1 gap-y-3 gap-x-8 p-4 md:grid-cols-2">
                {visible.map(f => {
                    const disp = displayValue(f.value);
                    const link = isUrl(f.value);
                    return (
                        <div key={f.column} className="flex gap-3 min-w-0">
                            <span className="w-44 text-xs font-semibold tracking-wider uppercase text-stone-400 shrink-0">
                                {humanizeColumn(f.column)}
                                {f.imported && f.crm_field && (
                                    <span className="block normal-case tracking-normal font-normal text-[10px] text-emerald-600">→ {f.crm_field}</span>
                                )}
                            </span>
                            {link ? (
                                <a href={f.value} target="_blank" rel="noopener noreferrer"
                                    className="text-sm text-blue-600 break-all hover:underline inline-flex items-start gap-1 min-w-0">
                                    <span className="truncate">{disp}</span>
                                    <ExternalLink className="w-3 h-3 shrink-0 mt-0.5" />
                                </a>
                            ) : (
                                <span className="text-sm break-words text-stone-700 min-w-0">{disp}</span>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}
